import { useCallback, useEffect, useState } from "react";
import { fetchAerocivilDiagnostics } from "../../../../../../weather/adminRadarActionsApi";
import WeatherHealthBadge from "../WeatherHealthBadge";

function GateRow({ gate }) {
  const status =
    gate.pass === true ? "healthy" : gate.status === "manual" ? "degraded" : "down";

  return (
    <div className="weather-radar-fallback__row">
      <span className="weather-radar-fallback__label">{gate.label}</span>
      <WeatherHealthBadge status={status} />
      {gate.detail ? (
        <span className="weather-radar-fallback__value muted">{gate.detail}</span>
      ) : null}
    </div>
  );
}

const WeatherAerocivilProductionRolloutPanel = ({ onToast }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  const loadDiagnostics = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchAerocivilDiagnostics({ refresh: false });
      setPayload(result);
    } catch (err) {
      setError(err.message || "Unable to load Aerocivil rollout diagnostics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDiagnostics();
  }, [loadDiagnostics]);

  const handleCopy = async () => {
    if (!payload?.rollout) {
      return;
    }

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload.rollout, null, 2));
      onToast?.({ tone: "success", message: "Aerocivil rollout diagnostics copied." });
    } catch {
      onToast?.({ tone: "error", message: "Unable to copy rollout diagnostics." });
    }
  };

  const rollout = payload?.rollout;
  const promotion = payload?.promotion;
  const monitoring = payload?.monitoring;
  const programClosure = payload?.programClosure;
  const health = payload?.aerocivilAviation;

  return (
    <div className="admin-dash__card panel-card weather-radar-fallback">
      <h2 className="admin-section-title">Aerocivil Production Rollout</h2>

      {promotion ? (
        <p className="weather-radar-fallback__summary" role="status">
          Promotion status: {promotion.promotionStatus}
          {promotion.readyForProduction ? " — automated gates clear" : ""}
        </p>
      ) : null}

      {monitoring?.alerts?.length ? (
        <div className="weather-radar-fallback__sources">
          {monitoring.alerts.map((alert) => (
            <div key={alert.code} className="weather-radar-fallback__row">
              <span className="weather-radar-fallback__label">{alert.code}</span>
              <WeatherHealthBadge
                status={alert.severity === "critical" ? "down" : "degraded"}
              />
              <span className="weather-radar-fallback__value muted">{alert.message}</span>
            </div>
          ))}
        </div>
      ) : null}

      {loading ? <p className="muted">Loading rollout diagnostics…</p> : null}
      {error ? <p className="weather-radar-health-notice">{error}</p> : null}

      {rollout ? (
        <>
          <div className="weather-radar-fallback__grid">
            <div className="weather-radar-fallback__row">
              <span className="weather-radar-fallback__label">Rollout status</span>
              <span className="weather-radar-fallback__value">{rollout.rolloutStatus}</span>
              <WeatherHealthBadge
                status={rollout.readyForPromotion ? "healthy" : "down"}
              />
            </div>
            <div className="weather-radar-fallback__row">
              <span className="weather-radar-fallback__label">Server enabled</span>
              <span className="weather-radar-fallback__value">
                {rollout.config?.serverEnabled ? "Yes" : "No"}
              </span>
            </div>
            <div className="weather-radar-fallback__row">
              <span className="weather-radar-fallback__label">Rollout scope</span>
              <span className="weather-radar-fallback__value">{rollout.config?.scope}</span>
            </div>
            <div className="weather-radar-fallback__row">
              <span className="weather-radar-fallback__label">Scope allowed</span>
              <span className="weather-radar-fallback__value">
                {rollout.config?.scopeAllowed ? "Yes" : "No"}
              </span>
            </div>
            <div className="weather-radar-fallback__row">
              <span className="weather-radar-fallback__label">Bounds confirmed</span>
              <span className="weather-radar-fallback__value">
                {rollout.config?.boundsConfirmed ? "Yes" : "Provisional"}
              </span>
            </div>
          </div>

          {health ? (
            <p className="weather-radar-fallback__summary" role="status">
              Pipeline health: {health.status} — {health.message}
            </p>
          ) : null}

          <div className="weather-radar-fallback__sources">
            {(promotion?.gates || rollout.gates)?.map((gate) => (
              <GateRow key={gate.id} gate={gate} />
            ))}
          </div>

          <p className="weather-radar-fallback__summary muted" role="note">
            Run <code>npm run weather:radar:aerocivil:rollout-check</code> then{" "}
            <code>npm run weather:radar:aerocivil:promote</code>. Monitor with{" "}
            <code>npm run weather:radar:aerocivil:monitor</code>. Sign-off with{" "}
            <code>npm run weather:radar:aerocivil:signoff-check</code>. See{" "}
            <code>docs/weather/aerocivil-production-rollout.md</code>.
          </p>

          {programClosure ? (
            <div className="weather-radar-fallback__sources">
              <div className="weather-radar-fallback__row">
                <span className="weather-radar-fallback__label">Program closure (A10)</span>
                <span className="weather-radar-fallback__value">{programClosure.programStatus}</span>
                <WeatherHealthBadge
                  status={
                    programClosure.programStatus === "program_complete"
                      ? "healthy"
                      : programClosure.readyForProgramClosure
                        ? "degraded"
                        : "down"
                  }
                />
              </div>
              {programClosure.gates?.slice(0, 4).map((gate) => (
                <GateRow key={gate.id} gate={gate} />
              ))}
            </div>
          ) : null}

          <div className="weather-radar-fallback__actions">
            <button type="button" className="btn btn--secondary" onClick={loadDiagnostics}>
              Refresh rollout diagnostics
            </button>
            <button type="button" className="btn btn--secondary" onClick={handleCopy}>
              Copy rollout JSON
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default WeatherAerocivilProductionRolloutPanel;
